"use client";
import {
  Building,
  Microscope,
  Building2,
  CheckCircle,
  Cloud,
  Database,
  Server,
  TrendingUp,
} from "lucide-react";
import { AnimatedGradientBackground, GradientPresets } from "@/components/Animation/GradientBackground";
import IconBox from "@/components/Icons/iconbox";

interface Example {
  client: string;
  project: string;
  description: string;
}

interface TechIcon {
  name: string;
  icon: any;
}

interface ImpactSection {
  id: string;
  icon: any;
  title: string;
  tagline: string;
  description: string;
  examples: Example[];
  howWeDeliver: string[];
  techIcons: TechIcon[];
}

const impactSections: ImpactSection[] = [
  {
    id: "financial-services",
    icon: Building,
    title: "Financial Services",
    tagline: "Navigating complexity with precision and trust.",
    description:
      "From regulatory reform to core-system modernisation, we have supported leading financial institutions in executing modernisation under extreme scrutiny. Our teams combine strategy, architecture, and data science to ensure programmes deliver compliance and commercial advantage simultaneously.",
    examples: [
      {
        client: "Lloyds Banking Group",
        project: "Wealth Migration & Data Transformation",
        description:
          "Migrated £40bn in pension portfolios onto FNZ's cloud platform, improving data integrity, reducing operational risk, and enabling the foundation for AI-powered wealth management across the Schroders partnership.",
      },
      {
        client: "UBS",
        project: "AI-Driven Risk Data Transformation",
        description:
          "Led cross-functional delivery to harmonise Credit Suisse NFR systems with UBS, accelerating AI integration and de-risking onboarding through scalable data unification.",
      },
      {
        client: "AIG",
        project: "Regulatory Realignment",
        description:
          "Executed secure data migrations and divestment programmes during corporate restructuring, meeting European compliance requirements under compressed delivery windows.",
      },
    ],
    howWeDeliver: [
      "Robust programme governance integrating technology, legal, and operational workstreams.",
      "Cloud-based data models enabling transparency and auditability.",
      "Automated testing, DevSecOps pipelines, and reconciliation controls.",
    ],
    techIcons: [
      { name: "AWS", icon: Cloud },
      { name: "Snowflake", icon: Database },
      { name: "Databricks", icon: Database },
      { name: "ServiceNow", icon: Server },
    ],
  },
  {
    id: "pharma-life-sciences",
    icon: Microscope,
    title: "Pharma & Life Sciences",
    tagline: "Accelerating discovery through intelligent data ecosystems.",
    description:
      "We partner with global life sciences organisations to build the data foundations for faster, smarter, and more ethical innovation. Our work enables real-time insight generation across R&D, manufacturing, and commercial functions — ensuring regulatory compliance and scientific integrity.",
    examples: [
      {
        client: "Zencode Consultancy",
        project: "AWS Architecture",
        description:
          "We designed and implemented a scalable, secure, and cost-efficient AWS architecture tailored to the client’s application and business needs. The solution leveraged core AWS services such as EC2, VPC, RDS, S3, and IAM to ensure high availability, data security, and seamless scalability. We focused on infrastructure best practices including network segmentation, automated backups, monitoring, and fault tolerance. The resulting architecture improved system reliability, reduced operational overhead, and provided a strong foundation for future growth."

      },

    ],
    howWeDeliver: [
      "Modern data architecture built on scalable, cloud-agnostic platforms.",
      "Ethical AI frameworks ensuring explainability in ML-driven research.",
      "Cross-functional data operating models connecting science, tech, and people.",
    ],
    techIcons: [
      { name: "Databricks", icon: Database },
      { name: "AWS", icon: Cloud },
      { name: "Python ML", icon: TrendingUp },
      { name: "Power BI", icon: TrendingUp },
    ],
  },
  {
    id: "public-sector",
    icon: Building2,
    title: "Public Sector",
    tagline: "Embedding digital capability into government delivery.",
    description:
      "Public institutions face challenges of scale, accountability, and legacy systems — yet the ambition to modernise has never been stronger. Luminar works with departments to implement projects that focus on citizens, utilise data, and are designed for long-term sustainability.",
    examples: [
      {
        client: "UK Government Agency",
        project: "Knowledge Transfer & Capability Uplift",
        description:
          "Designed and implemented a sustainable data platform with embedded training and governance, allowing internal teams to manage analytics independently post-delivery.",
      },
    ],
    howWeDeliver: [
      "Co-design of modernisation offices to ensure ownership and transparency.",
      "Human-centred service design supported by digital automation.",
      "Data ethics and privacy integrated from day one.",
    ],
    techIcons: [
      { name: "AWS GovCloud", icon: Cloud },
      { name: "ServiceNow", icon: Server },
      { name: "Snowflake", icon: Database },
      { name: "Tableau", icon: TrendingUp },
    ],
  },
];

export default function ImpactPage() {
  return (
    <main className="min-h-screen text-text-primary">
      <AnimatedGradientBackground {...GradientPresets.impact} />

      {/* HERO */}
      <section className="relative max-w-6xl mx-auto px-6">
        <div className="border border-standard pt-40 p-8 sm:p-12 transition-all duration-1000 ease-[cubic-bezier(0.19,1,0.22,1)] opacity-100 translate-y-0">
          <h1 className="h1 pt-24 mb-6">
            Results That <span className="text-text-secondary">Matter</span>
          </h1>
          <p className="body-lg mb-6">
            A UK based consultancy trusted globally. At Luminar, we define success by outcomes
            rather than deliverables.
          </p>
          <p className="body-md mb-4">
            Our work is measured by what changes after we leave: stronger systems, empowered teams,
            and measurable improvements in performance.
          </p>
          <p className="body-md">
            Each project is designed to create value that lasts, combining commercial impact with
            operational resilience and ethical innovation.
          </p>
        </div>
      </section>

      {/* IMPACT SECTIONS */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24">
        <div className="space-y-12 sm:space-y-16">
          {impactSections.map((impact, index) => {
            const colorClass = index % 2 === 0 ? "text-accentcolourteal" : "text-accentcolourorange";
            const bgColorClass = index % 2 === 0 ? "bg-accentcolourteal" : "bg-accentcolourorange";
            const borderColorClass =
              index % 2 === 0 ? "border-accentcolourteal/30" : "border-accentcolourorange/30";

            return (
              <div key={impact.id} id={impact.id} className="scroll-mt-24">
                {/* Section Header */}
                <div className="flex items-start gap-4 sm:gap-6 mb-6 sm:mb-8">
                  <IconBox icon={impact.icon} index={index} size="lg" />
                  <div className="flex-1">
                    <h2 className="h3 mb-2 sm:mb-3">{impact.title}</h2>
                    <p className="body-lg italic">{impact.tagline}</p>
                  </div>
                </div>

                {/* Content Card */}
                <div className="border border-standard backdrop-blur-sm p-6 sm:p-8">
                  <div className="space-y-6 sm:space-y-8">
                    {/* Description */}
                    <div>
                      <div className={`h-1 w-12 mb-4 sm:mb-6 ${bgColorClass}`} />
                      <p className="body-md leading-relaxed">{impact.description}</p>
                    </div>

                    {/* Examples of Impact */}
                    <div>
                      <h3 className={`h3 mb-4 sm:mb-6 ${colorClass}`}>Examples of Impact</h3>
                      <div className="space-y-6">
                        {impact.examples.map((example, idx) => (
                          <div key={idx} className={`border-l-4 ${borderColorClass} pl-4 sm:pl-6 py-2`}>
                            <h4 className="body-lg font-semibold text-text-primary mb-2">
                              {example.client} | {example.project}
                            </h4>
                            <p className="body-md leading-relaxed">
                              {example.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* How We Deliver */}
                    <div>
                      <h3 className={`h3 mb-4 ${colorClass}`}>How We Deliver</h3>
                      <ul className="space-y-3">
                        {impact.howWeDeliver.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle
                              className={`w-5 h-5 flex-shrink-0 mt-0.5 ${colorClass}`}
                            />
                            <span className="body-md">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Technologies */}
                    <div className="pt-4">
                      <h4 className="body-md font-semibold text-text-primary mb-4">
                        Technologies Used
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                        {impact.techIcons.map((tech, idx) => (
                          <div
                            key={idx}
                            className="flex flex-col items-center justify-center p-3 sm:p-4 border border-standard backdrop-blur-sm rounded-lg min-h-[100px] sm:min-h-[120px]"
                          >
                            <IconBox icon={tech.icon} index={index} size="sm" className="mb-2" />
                            <span className="body-md text-center break-words w-full">
                              {tech.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CROSS-CLIENT LEARNINGS */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24 mb-24">
        <div className="border border-standard backdrop-blur-sm p-8 sm:p-12">
          <h2 className="h2 text-center mb-6 sm:mb-8">Cross-Client Learnings</h2>
          <p className="body-lg text-center mb-8 sm:mb-12 max-w-3xl mx-auto">
            Across our portfolio, three constants define Luminar's delivery model:
          </p>

          <div className="grid sm:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="h-1 w-16 bg-accentcolourteal mx-auto mb-4 sm:mb-6" />
              <h3 className="h3 mb-2 sm:mb-4">Integrated Strategy and Delivery</h3>
            </div>

            <div className="text-center">
              <div className="h-1 w-16 bg-accentcolourteal mx-auto mb-4 sm:mb-6" />
              <h3 className="h3 mb-2 sm:mb-4">Technology as an Enabler, not the Answer</h3>
            </div>

            <div className="text-center">
              <div className="h-1 w-16 bg-accentcolourteal mx-auto mb-4 sm:mb-6" />
              <h3 className="h3 mb-2 sm:mb-4">Enduring Capability</h3>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}